// Templates de mensagens WhatsApp para reativação de usuários no painel admin
// Cada função recebe os dados do usuário e retorna mensagem pronta (texto puro)

const SITE_URL = "https://aipalpiteguru.mentepoderosa2030.com";

/**
 * Bloco padrão do GURU CHAT — incluído em todas as mensagens de WhatsApp.
 */
export const BLOCO_GURU_CHAT = `🤖 *NOVIDADE — GURU CHAT*
Uma IA que conversa com você em tempo real:
• Interpreta seu sonho e transforma em palpite
• Sugere o palpite do dia com base nos resultados
• Indica a melhor ferramenta para cada situação
• Tira dúvidas sobre grupos, dezenas, centenas e milhares
👉 ${SITE_URL}/guru-chat`;

interface UserData {
  first_name?: string | null;
  email?: string | null;
  whatsapp?: string | null;
  phone?: string | null;
  plan?: string | null;
  subscription_end_date?: string | null;
  created_at?: string | null;
}

const formatPlanName = (plan?: string | null): string => {
  if (!plan) return "Premium";
  const map: Record<string, string> = {
    basic: "Básico",
    premium: "Premium",
    vip: "VIP",
    quarterly: "Trimestral",
    annual: "Anual",
    free: "Free",
  };
  return map[plan] ?? plan.charAt(0).toUpperCase() + plan.slice(1);
};

const formatDateBR = (dateString?: string | null): string => {
  if (!dateString) return "recentemente";
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(dateString));
  } catch {
    return "recentemente";
  }
};

const daysSince = (dateString?: string | null): number => {
  if (!dateString) return 0;
  const ms = Date.now() - new Date(dateString).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
};

/**
 * Mensagem para usuários com plano vencido — oferece cupom VOLTA50
 */
export const buildExpiredMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  const plano = formatPlanName(user.plan);
  const venceu = formatDateBR(user.subscription_end_date);

  return `Olá ${nome}! 👋

Seu plano ${plano} no IA Palpite Guru venceu em ${venceu}.

Sentimos sua falta! 🎯 Para você voltar com tudo, separei uma OFERTA ESPECIAL:

🎁 50% OFF na renovação (cupom VOLTA50)
✅ Acesso ilimitado a todos os métodos
✅ Sistema IA do Bicho liberado
✅ Suporte prioritário

Renove agora: ${SITE_URL}/planos

${BLOCO_GURU_CHAT}

Posso te ajudar a escolher o melhor plano? 😊`;
};

/**
 * Mensagem para usuários que não confirmaram o email
 */
export const buildUnconfirmedMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  const email = user.email || "seu email";

  return `Olá ${nome}! 👋

Vi que você se cadastrou no IA Palpite Guru mas ainda não confirmou seu email. 📧

Para liberar 100% das funcionalidades, é só:
1️⃣ Abrir seu email (${email})
2️⃣ Clicar no link de confirmação que enviamos
3️⃣ Aproveitar todos os métodos premium!

Não recebeu? Posso reenviar agora mesmo.

${BLOCO_GURU_CHAT}

Qualquer dúvida estou aqui! 🎯`;
};

/**
 * Mensagem para leads frios (free há mais de 7 dias)
 */
export const buildColdLeadMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  const dias = daysSince(user.created_at);

  return `Olá ${nome}!

Faz ${dias} dias que você se cadastrou no IA Palpite Guru e ainda não testou tudo que preparamos pra você.

Hoje saíram resultados quentes 🔥 e adicionamos novos métodos exclusivos.

Volta lá: ${SITE_URL}

${BLOCO_GURU_CHAT}

Quer dica personalizada? Me chama!`;
};

/**
 * Normaliza telefone para padrão WhatsApp internacional (somente dígitos, com 55 BR)
 */
export const normalizeWhatsAppNumber = (raw?: string | null): string | null => {
  if (!raw) return null;
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 10) return null;
  // Se já começa com 55 (Brasil), mantém. Caso contrário adiciona.
  return digits.startsWith("55") ? digits : `55${digits}`;
};

/**
 * Constrói URL wa.me com mensagem pré-configurada
 */
export const buildWhatsAppUrl = (phone: string, message: string): string => {
  return `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
};

// ============================================================
// Acompanhamento do Usuário — mensagens de follow-up (não-leads)
// ============================================================

export type FollowUpType = "geral" | "feedback" | "ajuda" | "incentivo" | "beneficios_guru";

export const buildFollowUpGeneralMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  return `Olá ${nome}, tudo bem? Aqui é o suporte do IA Palpite Guru.

Estou passando para saber como está sendo sua experiência com o sistema.

Você conseguiu acessar as ferramentas e usar os métodos, estatísticas, puxadas e geração com IA?

${BLOCO_GURU_CHAT}

Se tiver qualquer dúvida ou dificuldade, posso te ajudar por aqui. 😊`;
};

export const buildFollowUpFeedbackMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  return `Olá ${nome}, tudo bem? Aqui é o suporte do IA Palpite Guru.

Estamos ouvindo alguns usuários para melhorar cada vez mais a plataforma.

Você poderia me dizer o que achou do IA Palpite Guru até agora?

${BLOCO_GURU_CHAT}

Sua opinião é muito importante para ajustarmos o sistema, melhorar as ferramentas e entregar uma experiência cada vez melhor. 🙏`;
};

export const buildFollowUpHelpMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  return `Olá ${nome}, tudo bem? Aqui é o suporte do IA Palpite Guru.

Estou passando para saber se você precisa de ajuda para usar alguma ferramenta da plataforma.

Temos métodos, estatísticas, puxadas, geração com IA e várias funções que podem te ajudar a usar o sistema de forma mais estratégica.

${BLOCO_GURU_CHAT}

Se quiser, posso te orientar por aqui. 🎯`;
};

export const buildFollowUpEncourageMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  return `Olá ${nome}, tudo bem? Aqui é o suporte do IA Palpite Guru.

Passando para lembrar que o IA Palpite Guru possui várias ferramentas disponíveis, como métodos, estatísticas, puxadas e geração de jogos com IA.

Aproveite para explorar o sistema e usar os recursos com estratégia.

${BLOCO_GURU_CHAT}

Se tiver dúvida sobre como usar alguma função, posso te ajudar por aqui. 🚀`;
};

/**
 * Apresenta benefícios premium + Guru Chat (IA) para usuários free que ainda não ativaram plano.
 * Convida a responder para receber condição especial.
 */
export const buildFollowUpBeneficiosGuruMessage = (user: UserData): string => {
  const nome = user.first_name?.trim() || "amigo(a)";
  return `Olá ${nome}, tudo bem? Aqui é do IA Palpite Guru. 👋

Vi que você criou sua conta mas ainda não ativou um plano — e quero te mostrar rapidinho o que você está perdendo:

🎯 Métodos exclusivos (Soma Chave de Ouro, Calculadora Chave de Ouro, Sistema IA do Bicho e mais 12)
📊 Estatísticas completas com frequência, atraso e padrões
🔮 Puxadas inteligentes a partir do último resultado
🤖 Gerador de palpites com IA treinada nos resultados

E a grande novidade: o GURU CHAT 🧠✨
Uma IA que conversa com você, interpreta seus sonhos, sugere o palpite do dia e ainda recomenda a melhor ferramenta pra cada situação — tudo em tempo real.

👉 Conheça: ${SITE_URL}/guru-chat
👉 Planos: ${SITE_URL}/planos

Me responde aqui que eu libero uma condição especial pra você começar hoje. 🚀`;
};
